import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListFilter,
  ShieldHalf,
  Search,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Input,
  MetricCard,
  Select,
  Table,
  type Column,
} from '@/components';
import { useActiveFocus, useData, useUI, inFocus } from '@/data/stores';
import { demoTimestamp, downloadJson } from '@/lib/export';
import { fmtRelative } from '@/lib/format';
import { severityColor, severityLabel } from '@/lib/color';
import type { Alert, AlertCategory, AlertStatus } from '@/types';
import './alerts.css';

export function AlertsView() {
  const navigate = useNavigate();
  const allAlerts = useData((s) => s.alerts);
  const assets = useData((s) => s.assets);
  const notify = useUI((s) => s.notifyAction);
  const focus = useActiveFocus();
  const assetById = useMemo(() => new Map(assets.map((a) => [a.asset_id, a])), [assets]);
  const alerts = useMemo(() => {
    if (focus.id === 'global') return allAlerts;
    return allAlerts.filter((al) => {
      if (inFocus(focus, al)) return true;
      // Also include alerts whose linked assets fall in focus (cross-region cover)
      return al.asset_ids.some((id) => {
        const asset = assetById.get(id);
        return asset ? inFocus(focus, asset) : false;
      });
    });
  }, [allAlerts, assetById, focus]);
  const [q, setQ] = useState('');
  const [sev, setSev] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [cat, setCat] = useState<string>('');

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return alerts.filter((a) => {
      if (sev && a.severity !== Number(sev)) return false;
      if (status && a.status !== status) return false;
      if (cat && a.category !== cat) return false;
      if (lower) {
        const blob = `${a.title} ${a.description} ${a.alert_id} ${a.threat_actor ?? ''}`.toLowerCase();
        if (!blob.includes(lower)) return false;
      }
      return true;
    });
  }, [alerts, q, sev, status, cat]);

  const counts = useMemo(() => {
    const sevCount: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let open = 0;
    let contained = 0;
    let resolved = 0;
    for (const a of alerts) {
      sevCount[a.severity] = (sevCount[a.severity] ?? 0) + 1;
      if (a.status === 'open' || a.status === 'investigating') open += 1;
      else if (a.status === 'contained') contained += 1;
      else if (a.status === 'resolved') resolved += 1;
    }
    return { sevCount, open, contained, resolved, total: alerts.length };
  }, [alerts]);

  const columns: Column<Alert>[] = [
    {
      key: 'severity',
      header: 'Sev',
      width: '90px',
      sortValue: (a) => -a.severity,
      render: (a) => (
        <span
          className="iris-alert-sev"
          style={{ color: severityColor(a.severity) }}
        >
          ● {severityLabel(a.severity)}
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Alert',
      sortValue: (a) => a.title,
      render: (a) => (
        <div>
          <div>{a.title}</div>
          <div className="caption mono">
            {a.alert_id}
            {a.protocol ? ` · ${a.protocol}` : ''}
            {a.zone ? ` · ${a.zone}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'asset',
      header: 'Asset(s)',
      width: '14%',
      mono: true,
      render: (a) => a.asset_ids.join(', '),
    },
    {
      key: 'category',
      header: 'Category',
      width: '11%',
      sortValue: (a) => a.category,
      render: (a) => (
        <Badge color={categoryColor(a.category)}>{a.category}</Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '11%',
      sortValue: (a) => a.status,
      render: (a) => (
        <Badge color={statusColor(a.status)}>{a.status.replace(/_/g, ' ')}</Badge>
      ),
    },
    {
      key: 'time',
      header: 'Detected',
      width: '12%',
      mono: true,
      sortValue: (a) => a.detected_at,
      render: (a) => fmtRelative(a.detected_at),
    },
    {
      key: 'conf',
      header: 'Conf',
      width: '6%',
      mono: true,
      align: 'right',
      sortValue: (a) => a.confidence,
      render: (a) => `${a.confidence}%`,
    },
    {
      key: 'enf',
      header: 'Action',
      width: '10%',
      sortValue: (a) => a.enforcement_action ?? '',
      render: (a) =>
        a.enforcement_action ? (
          <Badge color="var(--status-info)">{a.enforcement_action}</Badge>
        ) : (
          <span className="caption">-</span>
        ),
    },
  ];

  return (
    <div className="iris-page">
      <header className="iris-page-header">
        <div>
          <h1>Alerts & Incidents</h1>
          <p className="caption">
            Layer 3 - Cyber, physical, and cyber-physical correlation. All alerts MITRE-mapped,
            confidence-scored, and linked to the affected assets.
          </p>
        </div>
        <div className="iris-page-actions">
          <Button
            icon={<ListFilter size={14} strokeWidth={1.5} />}
            onClick={() => {
              setSev('5');
              setStatus('open');
              setCat('');
              notify({
                title: 'Saved filter applied',
                body: 'Showing open critical alerts for the active focus mode.',
              });
            }}
          >
            Saved filters
          </Button>
          <Button
            variant="primary"
            icon={<ShieldHalf size={14} strokeWidth={1.5} />}
            onClick={() => {
              downloadJson(`iris-siem-handoff-${demoTimestamp()}.json`, {
                generated_at: new Date().toISOString(),
                focus_mode: focus.id,
                query: { search: q, severity: sev, status, category: cat },
                alert_count: filtered.length,
                alerts: filtered.slice(0, 100),
              });
              notify({
                kind: 'success',
                title: 'SIEM handoff prepared',
                body: `${filtered.length} alert records exported with the current filter context.`,
              });
            }}
          >
            Open SIEM
          </Button>
        </div>
      </header>

      <section className="iris-page-metrics">
        <MetricCard
          label="Open"
          value={counts.open}
          status="var(--status-critical)"
          caption="open + investigating"
        />
        <MetricCard
          label="Contained"
          value={counts.contained}
          status="var(--status-info)"
          caption="enforcement applied"
        />
        <MetricCard
          label="Critical (sev 5)"
          value={counts.sevCount[5] ?? 0}
          status="var(--status-critical)"
        />
        <MetricCard
          label="High (sev 4)"
          value={counts.sevCount[4] ?? 0}
          status="var(--status-degraded)"
        />
        <MetricCard
          label="Resolved 24h"
          value={counts.resolved}
          status="var(--status-operational)"
        />
        <MetricCard label="Total" value={counts.total} />
      </section>

      <Card title="Filters">
        <div className="iris-alert-filters">
          <Input
            label="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Title, description, ID, threat actor…"
            startIcon={<Search size={14} strokeWidth={1.5} />}
          />
          <Select
            label="Severity"
            value={sev}
            onChange={(e) => setSev(e.target.value)}
            options={[
              { value: '', label: 'All' },
              { value: '5', label: '5 - Critical' },
              { value: '4', label: '4 - High' },
              { value: '3', label: '3 - Medium' },
              { value: '2', label: '2 - Low' },
              { value: '1', label: '1 - Informational' },
            ]}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { value: '', label: 'All' },
              { value: 'open', label: 'Open' },
              { value: 'investigating', label: 'Investigating' },
              { value: 'contained', label: 'Contained' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'dismissed', label: 'Dismissed' },
            ]}
          />
          <Select
            label="Category"
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            options={[
              { value: '', label: 'All' },
              { value: 'cyber', label: 'Cyber' },
              { value: 'physical', label: 'Physical' },
              { value: 'cyber_physical', label: 'Cyber-physical' },
              { value: 'process', label: 'Process' },
              { value: 'compliance', label: 'Compliance' },
              { value: 'environmental', label: 'Environmental' },
              { value: 'geopolitical', label: 'Geopolitical' },
              { value: 'supply_chain', label: 'Supply chain' },
            ]}
          />
        </div>
      </Card>

      <div className="iris-page-table">
        <Table
          rows={filtered}
          columns={columns}
          rowKey={(a) => a.alert_id}
          onRowClick={(a) => navigate(`/alerts/${a.alert_id}`)}
          defaultSort={{ key: 'severity', dir: 'asc' }}
        />
      </div>
    </div>
  );
}

function categoryColor(c: AlertCategory): string {
  switch (c) {
    case 'cyber':
      return 'var(--status-info)';
    case 'physical':
      return 'var(--status-degraded)';
    case 'cyber_physical':
      return 'var(--status-critical)';
    case 'process':
      return 'var(--text-secondary)';
    case 'compliance':
      return 'var(--text-secondary)';
    case 'environmental':
      return 'var(--text-secondary)';
    case 'geopolitical':
      return 'var(--status-degraded)';
    case 'supply_chain':
      return 'var(--status-critical)';
  }
}

function statusColor(s: AlertStatus): string {
  switch (s) {
    case 'open':
      return 'var(--status-critical)';
    case 'investigating':
      return 'var(--status-degraded)';
    case 'contained':
      return 'var(--status-info)';
    case 'resolved':
      return 'var(--status-operational)';
    case 'dismissed':
      return 'var(--text-secondary)';
  }
}
